import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, ProgressBar } from 'react-bootstrap';
import type { LeadCloudFileType } from '@econtractor/types';
import {
    deleteCloudflareFiles,
    getCloudflareUploadUrls,
    linkCloudflareFiles,
    type CloudUploadScope,
} from 'api/apiCloudflare';
import { useToast } from 'context/ToastContext';
import spinnerTitle from 'components/others/spinnerTitle';
import FilesList from './FilesList';
import { toStoredCloudFile } from './helper/filesUtils';
import { uploadFilesWithOverallProgress } from 'utils/helpers/featchFiles/uploadFileWithProgress';
import SolarIcon from 'components/icons/SolarIcon';

interface FilesContainerProps {
    files: LeadCloudFileType[];
    scope: CloudUploadScope;
    imageColProps?: {
        xs?: number;
        sm?: number;
        md?: number;
        lg?: number;
        xl?: number;
        xxl?: number;
    };
    canEdit?: boolean;
    addButtonLabel: string;
    uploadingLabel: string;
    saveFirstMessage: string;
    errorMessage: string;
    uploadSuccessMessage: string;
    onFilesChange: (nextFiles: LeadCloudFileType[]) => Promise<boolean>;
    /** Якщо сутність ще не збережена — створити/зберегти і повернути оновлений scope */
    onEnsureEntity?: () => Promise<CloudUploadScope | false>;
    /** Показувати зображення як посилання, без прев’ю */
    noShowPreviewImages?: boolean;
}

const getScopeEntityId = (scope: CloudUploadScope): number =>
    Number(
        scope?.avatarId ||
            scope?.paymentId ||
            scope?.productId ||
            scope?.serviceId ||
            scope?.objectId ||
            scope?.eventId ||
            scope?.leadId ||
            scope?.taskId ||
            scope?.toolId ||
            scope?.toolTransferId ||
            scope?.toolCalibrationId ||
            scope?.clientCustomerId ||
            0,
    );

const FilesContainer = ({
    files,
    scope,
    imageColProps,
    canEdit,
    addButtonLabel,
    uploadingLabel,
    saveFirstMessage,
    errorMessage,
    uploadSuccessMessage,
    onFilesChange,
    onEnsureEntity,
    noShowPreviewImages,
}: FilesContainerProps) => {
    const { showToast } = useToast();
    const [progress, setProgress] = useState<number | false>(false);
    const [ensuring, setEnsuring] = useState(false);
    const [deletingKey, setDeletingKey] = useState<string | null>(null);
    const isBusy = progress !== false || ensuring;
    const refInputFile = useRef<HTMLInputElement>(null);
    const scopeRef = useRef(scope);

    useEffect(() => {
        const incomingId = getScopeEntityId(scope);
        const currentId = getScopeEntityId(scopeRef.current);
        if (incomingId > 0 || currentId <= 0) {
            scopeRef.current = scope;
        }
    }, [scope]);

    const entityId = useMemo(() => getScopeEntityId(scope), [scope]);
    const galleryId = useMemo(
        () =>
            `files-${
                scope?.avatarId
                    ? 'avatar'
                    : scope?.paymentId
                      ? 'payment'
                      : scope?.productId
                        ? 'product'
                        : scope?.serviceId
                          ? 'service'
                          : scope?.objectId
                            ? 'object'
                            : scope?.taskId
                              ? 'task'
                              : scope?.toolId
                                ? 'tool'
                                : scope?.toolTransferId
                                  ? 'tool-transfer'
                                  : scope?.toolCalibrationId
                                    ? 'tool-calibration'
                                    : scope?.clientCustomerId
                                      ? 'client-customer'
                                      : 'entity'
            }-${entityId || 'new'}`,
        [
            entityId,
            scope?.avatarId,
            scope?.objectId,
            scope?.paymentId,
            scope?.productId,
            scope?.serviceId,
            scope?.taskId,
            scope?.toolId,
            scope?.toolTransferId,
            scope?.toolCalibrationId,
            scope?.clientCustomerId,
        ],
    );

    const applyNextFiles = useCallback(
        async (nextFiles: LeadCloudFileType[]): Promise<boolean> => {
            const stat = await onFilesChange(nextFiles);
            if (!stat) showToast(errorMessage, 'danger');
            return stat;
        },
        [onFilesChange, showToast, errorMessage],
    );

    const uploadSelectedFiles = useCallback(
        async (selectedFiles: File[]) => {
            if (!selectedFiles.length) return;

            const activeScope = scopeRef.current;
            if (getScopeEntityId(activeScope) <= 0) {
                showToast(saveFirstMessage, 'warning');
                return;
            }

            const filesMeta = selectedFiles.map((file) => ({
                fileName: file.name,
                contentType: file.type || 'application/octet-stream',
                fileSize: file.size,
            }));

            const uploadConfigs = await getCloudflareUploadUrls(filesMeta, activeScope);
            if (!uploadConfigs || uploadConfigs.length !== selectedFiles.length) {
                showToast(errorMessage, 'danger');
                return;
            }

            setProgress(1);
            const uploaded = await uploadFilesWithOverallProgress(selectedFiles, uploadConfigs, setProgress);
            setProgress(false);

            const successFiles = uploaded.filter((item) => item.success).map((item) => toStoredCloudFile(item.config));
            if (!successFiles.length) {
                showToast(errorMessage, 'danger');
                return;
            }

            const linked = await linkCloudflareFiles(successFiles, activeScope);
            if (!linked) {
                showToast(errorMessage, 'danger');
                return;
            }

            const nextFiles = [...files, ...successFiles];
            const saved = await applyNextFiles(nextFiles);
            if (!saved) return;

            showToast(uploadSuccessMessage, 'success');
        },
        [saveFirstMessage, files, applyNextFiles, showToast, uploadSuccessMessage, errorMessage],
    );

    const handleChangeFile = useCallback(async () => {
        const selectedFiles = refInputFile.current?.files;
        if (!selectedFiles?.length) return;
        const filesList = Array.from(selectedFiles);
        if (refInputFile.current) refInputFile.current.value = '';
        await uploadSelectedFiles(filesList);
    }, [uploadSelectedFiles]);

    const openFileInput = useCallback(async () => {
        if (!refInputFile.current || isBusy) return;

        if (getScopeEntityId(scopeRef.current) <= 0) {
            if (!onEnsureEntity) {
                showToast(saveFirstMessage, 'warning');
                return;
            }
            setEnsuring(true);
            const ensuredScope = await onEnsureEntity();
            setEnsuring(false);
            if (!ensuredScope) return;
            scopeRef.current = { ...scopeRef.current, ...ensuredScope };
            if (getScopeEntityId(scopeRef.current) <= 0) {
                showToast(saveFirstMessage, 'warning');
                return;
            }
        }

        refInputFile.current.value = '';
        refInputFile.current.click();
    }, [isBusy, onEnsureEntity, saveFirstMessage, showToast]);

    const handleDeleteFile = useCallback(
        async (file: LeadCloudFileType) => {
            const activeScope = scopeRef.current;
            if (getScopeEntityId(activeScope) <= 0) return;

            setDeletingKey(file.storageKey);
            const stat = await deleteCloudflareFiles({
                storageKeys: [file.storageKey],
                scope: activeScope,
            });
            if (!stat) {
                setDeletingKey(null);
                showToast(errorMessage, 'danger');
                return;
            }

            const nextFiles = files.filter((item) => item.storageKey !== file.storageKey);
            const saved = await applyNextFiles(nextFiles);
            setDeletingKey(null);
            if (!saved) return;
        },
        [files, applyNextFiles, showToast, errorMessage],
    );

    return (
        <div className="text-center">
            <input type="file" ref={refInputFile} onChange={handleChangeFile} className="d-none" multiple />

            <FilesList
                files={files}
                galleryId={galleryId}
                imageColProps={imageColProps}
                canDelete={canEdit}
                deletingKey={deletingKey}
                onDelete={handleDeleteFile}
                noShowPreviewImages={noShowPreviewImages}
            />

            {progress !== false && (
                <div className="mx-auto mt-3 px-2" style={{ maxWidth: 360 }}>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                        <small className="text-muted">{uploadingLabel}</small>
                        <small className="text-muted fw-semibold">{progress}%</small>
                    </div>
                    <ProgressBar
                        now={Number(progress)}
                        variant="info"
                        animated
                        style={{ height: 6, borderRadius: 999 }}
                    />
                </div>
            )}

            {canEdit && (
                <Button variant="outline-primary" className="mt-3 px-4" disabled={isBusy} onClick={() => void openFileInput()}>
                    {spinnerTitle(
                        <>
                            <SolarIcon name="plus-circle" className="fs-14 me-2" />
                            {addButtonLabel}
                        </>,
                        isBusy,
                        uploadingLabel,
                    )}
                </Button>
            )}
        </div>
    );
};

export default FilesContainer;
